// roomSnapping.ts - Utility for snapping rooms to each other when walls are close

interface Point {
  x: number;
  z: number;
}

interface Room {
  id: string;
  room_type: string;
  area: number;
  height: number;
  width: number;
  floor_polygon: Point[];
}

interface Edge {
  start: Point;
  end: Point;
  roomId: string;
}

interface SnapResult {
  shouldSnap: boolean;
  deltaX: number;
  deltaZ: number;
  snapEdge?: {
    movingRoom: Edge;
    targetRoom: Edge;
  };
}

// Distance threshold for snapping (in floor plan units)
const SNAP_THRESHOLD = 3; // Reduced for less aggressive snapping

/**
 * Gets all edges of a room as line segments
 */
function getRoomEdges(room: Room): Edge[] {
  const edges: Edge[] = [];
  const polygon = room.floor_polygon;
  
  for (let i = 0; i < polygon.length; i++) {
    const start = polygon[i];
    const end = polygon[(i + 1) % polygon.length];
    edges.push({
      start,
      end,
      roomId: room.id,
    });
  }
  
  return edges;
}

/**
 * Calculate the perpendicular distance from a point to a line segment
 */
function pointToSegmentDistance(
  point: Point,
  segStart: Point,
  segEnd: Point
): number {
  const dx = segEnd.x - segStart.x;
  const dz = segEnd.z - segStart.z;
  const lengthSquared = dx * dx + dz * dz;
  
  if (lengthSquared === 0) {
    // Segment is a point
    const pdx = point.x - segStart.x;
    const pdz = point.z - segStart.z;
    return Math.sqrt(pdx * pdx + pdz * pdz);
  }
  
  // Calculate projection parameter
  const t = Math.max(
    0,
    Math.min(
      1,
      ((point.x - segStart.x) * dx + (point.z - segStart.z) * dz) / lengthSquared
    )
  );
  
  // Find the closest point on the segment
  const closestX = segStart.x + t * dx;
  const closestZ = segStart.z + t * dz;
  
  // Calculate distance
  const distX = point.x - closestX;
  const distZ = point.z - closestZ;
  
  return Math.sqrt(distX * distX + distZ * distZ);
}

/**
 * Calculate the minimum distance between two line segments
 */
function segmentToSegmentDistance(
  seg1Start: Point,
  seg1End: Point,
  seg2Start: Point,
  seg2End: Point
): number {
  // Check all four combinations of endpoints to segment distances
  const d1 = pointToSegmentDistance(seg1Start, seg2Start, seg2End);
  const d2 = pointToSegmentDistance(seg1End, seg2Start, seg2End);
  const d3 = pointToSegmentDistance(seg2Start, seg1Start, seg1End);
  const d4 = pointToSegmentDistance(seg2End, seg1Start, seg1End);
  
  return Math.min(d1, d2, d3, d4);
}

/**
 * Check if two edges are perfectly parallel (within 2 degrees)
 */
function areEdgesParallel(edge1: Edge, edge2: Edge, angleThreshold = 2): boolean {
  const dx1 = edge1.end.x - edge1.start.x;
  const dz1 = edge1.end.z - edge1.start.z;
  const dx2 = edge2.end.x - edge2.start.x;
  const dz2 = edge2.end.z - edge2.start.z;
  
  const len1 = Math.sqrt(dx1 * dx1 + dz1 * dz1);
  const len2 = Math.sqrt(dx2 * dx2 + dz2 * dz2);
  
  if (len1 === 0 || len2 === 0) return false;
  
  // Normalize
  const nx1 = dx1 / len1;
  const nz1 = dz1 / len1;
  const nx2 = dx2 / len2;
  const nz2 = dz2 / len2;
  
  // Calculate dot product (cosine of angle)
  const dotProduct = Math.abs(nx1 * nx2 + nz1 * nz2);
  
  // Convert angle threshold to cosine
  const cosThreshold = Math.cos((angleThreshold * Math.PI) / 180);
  
  // Edges are parallel if dot product is close to 1 (or -1)
  return dotProduct >= cosThreshold;
}

/**
 * Check if two edges have significant overlap when projected onto a common axis
 */
function doEdgesOverlap(edge1: Edge, edge2: Edge): boolean {
  // Get direction vector of edge1
  const dx = edge1.end.x - edge1.start.x;
  const dz = edge1.end.z - edge1.start.z;
  const len = Math.sqrt(dx * dx + dz * dz);
  
  if (len === 0) return false;
  
  // Normalized direction
  const dirX = dx / len;
  const dirZ = dz / len;
  
  // Project all four points onto the edge1 direction
  const projectPoint = (p: Point) => p.x * dirX + p.z * dirZ;
  
  const proj1Start = projectPoint(edge1.start);
  const proj1End = projectPoint(edge1.end);
  const proj2Start = projectPoint(edge2.start);
  const proj2End = projectPoint(edge2.end);
  
  // Get min/max for each edge
  const min1 = Math.min(proj1Start, proj1End);
  const max1 = Math.max(proj1Start, proj1End);
  const min2 = Math.min(proj2Start, proj2End);
  const max2 = Math.max(proj2Start, proj2End);
  
  // Check if there's overlap
  const overlapStart = Math.max(min1, min2);
  const overlapEnd = Math.min(max1, max2);
  const overlapLength = overlapEnd - overlapStart;
  
  // Require at least 30% overlap of the shorter edge
  const edge1Length = max1 - min1;
  const edge2Length = max2 - min2;
  const minEdgeLength = Math.min(edge1Length, edge2Length);
  
  return overlapLength > minEdgeLength * 0.3;
}

/**
 * Calculate the snap delta to align two parallel edges
 */
function calculateSnapDelta(movingEdge: Edge, targetEdge: Edge): { deltaX: number; deltaZ: number } {
  // Get the midpoints of both edges
  const movingMidX = (movingEdge.start.x + movingEdge.end.x) / 2;
  const movingMidZ = (movingEdge.start.z + movingEdge.end.z) / 2;
  
  const targetMidX = (targetEdge.start.x + targetEdge.end.x) / 2;
  const targetMidZ = (targetEdge.start.z + targetEdge.end.z) / 2;
  
  // Get the direction vector of the target edge
  const dx = targetEdge.end.x - targetEdge.start.x;
  const dz = targetEdge.end.z - targetEdge.start.z;
  const len = Math.sqrt(dx * dx + dz * dz);
  
  if (len === 0) return { deltaX: 0, deltaZ: 0 };
  
  // Normalized direction
  const dirX = dx / len;
  const dirZ = dz / len;
  
  // Perpendicular vector (normal)
  const normalX = -dirZ;
  const normalZ = dirX;
  
  // Vector from target midpoint to moving midpoint
  const toMovingX = movingMidX - targetMidX;
  const toMovingZ = movingMidZ - targetMidZ;
  
  // Project this vector onto the normal to get the perpendicular distance
  const perpDist = toMovingX * normalX + toMovingZ * normalZ;
  
  // The snap delta should move the moving edge to align with the target edge
  return {
    deltaX: -perpDist * normalX,
    deltaZ: -perpDist * normalZ,
  };
}

/**
 * Find if any wall of the moving rooms should snap to walls of static rooms
 * Returns the combined snap delta to align to all nearby parallel walls
 */
export function calculateRoomSnapDelta(
  movingRoomIds: string[],
  allRooms: Room[]
): SnapResult {
  // Get moving rooms and static rooms
  const movingRooms = allRooms.filter((room) => movingRoomIds.includes(room.id));
  const staticRooms = allRooms.filter(
    (room) => !movingRoomIds.includes(room.id) && room.room_type !== "Wall" && room.room_type !== "Boundary"
  );
  
  if (staticRooms.length === 0) {
    return { shouldSnap: false, deltaX: 0, deltaZ: 0 };
  }
  
  // Find all potential snap candidates
  const snapCandidates: {
    distance: number;
    deltaX: number;
    deltaZ: number;
    movingEdge: Edge;
    targetEdge: Edge;
  }[] = [];
  
  // Check each moving room against each static room
  for (const movingRoom of movingRooms) {
    const movingEdges = getRoomEdges(movingRoom);
    
    for (const staticRoom of staticRooms) {
      const staticEdges = getRoomEdges(staticRoom);
      
      // Check each edge of moving room against each edge of static room
      for (const movingEdge of movingEdges) {
        for (const staticEdge of staticEdges) {
          // Only consider perfectly parallel edges
          if (!areEdgesParallel(movingEdge, staticEdge)) continue;
          
          // Only consider edges that have significant overlap
          if (!doEdgesOverlap(movingEdge, staticEdge)) continue;
          
          // Calculate distance between edges
          const distance = segmentToSegmentDistance(
            movingEdge.start,
            movingEdge.end,
            staticEdge.start,
            staticEdge.end
          );
          
          // If within snap threshold, add as candidate
          if (distance < SNAP_THRESHOLD) {
            const snapDelta = calculateSnapDelta(movingEdge, staticEdge);
            snapCandidates.push({
              distance,
              deltaX: snapDelta.deltaX,
              deltaZ: snapDelta.deltaZ,
              movingEdge,
              targetEdge: staticEdge,
            });
          }
        }
      }
    }
  }
  
  // If we found snap candidates
  if (snapCandidates.length > 0) {
    // Sort by distance (closest first)
    snapCandidates.sort((a, b) => a.distance - b.distance);
    
    // Strategy: Apply snaps independently for X and Z axes
    let finalDeltaX = 0;
    let finalDeltaZ = 0;
    let hasXSnap = false;
    let hasZSnap = false;
    
    for (const candidate of snapCandidates) {
      // Determine if this is primarily an X-axis or Z-axis snap
      const absX = Math.abs(candidate.deltaX);
      const absZ = Math.abs(candidate.deltaZ);
      
      // If primarily X-axis movement and we haven't snapped X yet
      if (absX > absZ && !hasXSnap) {
        finalDeltaX = candidate.deltaX;
        hasXSnap = true;
      }
      // If primarily Z-axis movement and we haven't snapped Z yet
      else if (absZ > absX && !hasZSnap) {
        finalDeltaZ = candidate.deltaZ;
        hasZSnap = true;
      }
      
      // If we've snapped both axes, we're done
      if (hasXSnap && hasZSnap) break;
    }
    
    // Use the closest snap if we only found one axis
    if (!hasXSnap && !hasZSnap) {
      return {
        shouldSnap: true,
        deltaX: snapCandidates[0].deltaX,
        deltaZ: snapCandidates[0].deltaZ,
        snapEdge: {
          movingRoom: snapCandidates[0].movingEdge,
          targetRoom: snapCandidates[0].targetEdge,
        },
      };
    }
    
    return {
      shouldSnap: true,
      deltaX: finalDeltaX,
      deltaZ: finalDeltaZ,
    };
  }
  
  return { shouldSnap: false, deltaX: 0, deltaZ: 0 };
}

/**
 * Apply snap adjustment to room positions
 */
export function applySnapToRooms(
  rooms: Room[],
  movingRoomIds: string[],
  snapDelta: { deltaX: number; deltaZ: number }
): Room[] {
  return rooms.map((room) => {
    if (!movingRoomIds.includes(room.id)) return room;
    
    return {
      ...room,
      floor_polygon: room.floor_polygon.map((point) => ({
        x: point.x + snapDelta.deltaX,
        z: point.z + snapDelta.deltaZ,
      })),
    };
  });
}

/**
 * Calculate snap delta for a single edge being dragged
 * Used when resizing a room by dragging an edge towards other rooms
 */
export function calculateEdgeSnapDelta(
  roomId: string,
  edgeStart: Point,
  edgeEnd: Point,
  allRooms: Room[]
): { deltaX: number; deltaZ: number } {
  const movingEdge: Edge = {
    start: edgeStart,
    end: edgeEnd,
    roomId: roomId,
  };
  
  // Get all other rooms (exclude the room being resized)
  const otherRooms = allRooms.filter(
    (room) => room.id !== roomId && room.room_type !== "Wall" && room.room_type !== "Boundary"
  );
  
  if (otherRooms.length === 0) {
    return { deltaX: 0, deltaZ: 0 };
  }
  
  let closestDistance = Infinity;
  let bestSnapDelta = { deltaX: 0, deltaZ: 0 };
  
  // Check the moving edge against all edges of other rooms
  for (const otherRoom of otherRooms) {
    const otherEdges = getRoomEdges(otherRoom);
    
    for (const otherEdge of otherEdges) {
      // Only consider perfectly parallel edges
      if (!areEdgesParallel(movingEdge, otherEdge)) continue;
      
      // Only consider edges that have significant overlap
      if (!doEdgesOverlap(movingEdge, otherEdge)) continue;
      
      // Calculate distance between edges
      const distance = segmentToSegmentDistance(
        movingEdge.start,
        movingEdge.end,
        otherEdge.start,
        otherEdge.end
      );
      
      // If within snap threshold and closer than previous candidates
      if (distance < SNAP_THRESHOLD && distance < closestDistance) {
        closestDistance = distance;
        bestSnapDelta = calculateSnapDelta(movingEdge, otherEdge);
      }
    }
  }
  
  // Return the snap delta if we found a good candidate
  if (closestDistance < SNAP_THRESHOLD) {
    return bestSnapDelta;
  }
  
  return { deltaX: 0, deltaZ: 0 };
}

/**
 * Check if two edges are the same (shared wall between rooms)
 * Two edges are considered shared if their endpoints are very close (within tolerance)
 */
function areEdgesShared(edge1: Edge, edge2: Edge, tolerance = 4.0): boolean {
  // Check if start/end of edge1 match start/end of edge2 (or reversed)
  const dist1 = Math.sqrt(
    Math.pow(edge1.start.x - edge2.start.x, 2) + 
    Math.pow(edge1.start.z - edge2.start.z, 2)
  );
  const dist2 = Math.sqrt(
    Math.pow(edge1.end.x - edge2.end.x, 2) + 
    Math.pow(edge1.end.z - edge2.end.z, 2)
  );
  
  // Check forward direction
  if (dist1 < tolerance && dist2 < tolerance) return true;
  
  // Check reverse direction
  const dist3 = Math.sqrt(
    Math.pow(edge1.start.x - edge2.end.x, 2) + 
    Math.pow(edge1.start.z - edge2.end.z, 2)
  );
  const dist4 = Math.sqrt(
    Math.pow(edge1.end.x - edge2.start.x, 2) + 
    Math.pow(edge1.end.z - edge2.start.z, 2)
  );
  
  if (dist3 < tolerance && dist4 < tolerance) return true;
  
  return false;
}

/**
 * Find all rooms that share a wall with the given room
 */
export function findRoomsWithSharedWalls(
  roomId: string,
  allRooms: Room[]
): { roomId: string; sharedEdgeIndices: { room1EdgeIdx: number; room2EdgeIdx: number } }[] {
  const targetRoom = allRooms.find((r) => r.id === roomId);
  if (!targetRoom) return [];
  
  const targetEdges = getRoomEdges(targetRoom);
  const sharedRooms: { 
    roomId: string; 
    sharedEdgeIndices: { room1EdgeIdx: number; room2EdgeIdx: number } 
  }[] = [];
  
  for (const otherRoom of allRooms) {
    if (otherRoom.id === roomId) continue;
    if (otherRoom.room_type === "Wall" || otherRoom.room_type === "Boundary") continue;
    
    const otherEdges = getRoomEdges(otherRoom);
    
    for (let i = 0; i < targetEdges.length; i++) {
      for (let j = 0; j < otherEdges.length; j++) {
        if (areEdgesShared(targetEdges[i], otherEdges[j])) {
          sharedRooms.push({
            roomId: otherRoom.id,
            sharedEdgeIndices: {
              room1EdgeIdx: i,
              room2EdgeIdx: j,
            },
          });
        }
      }
    }
  }
  
  return sharedRooms;
}

/**
 * Check if two rooms share a wall (are snapped together)
 */
export function doRoomsShareWall(room1: Room, room2: Room, tolerance = 4.0): boolean {
  const edges1 = getRoomEdges(room1);
  const edges2 = getRoomEdges(room2);
  
  for (const edge1 of edges1) {
    for (const edge2 of edges2) {
      if (areEdgesShared(edge1, edge2, tolerance)) {
        return true;
      }
    }
  }
  
  return false;
}